import React from 'react';
import { Link } from 'react-router-dom';
import { Coins, Search, CreditCard, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLeadBalance } from '@/lib/creditsBus';
import { formatPeriodEnd } from '@/lib/leadBalance';
import { CREDITS_PATH } from '@/data/contractorNav';

/**
 * PRIORITY #1 on the contractor dashboard: how many leads can I unlock right
 * now? One compact bar (phone-first) — number, plan, renewal, and the two
 * actions that matter: Find Leads and Get more credits.
 */
const CreditsStrip: React.FC = () => {
  const { t, path, lang } = useI18n();
  const es = lang === 'es';
  const { profile } = useAuth();
  const { balance } = useLeadBalance(profile?.id);
  const n = balance.spendable;

  return (
    <section
      aria-label={es ? 'Sus créditos' : 'Your credits'}
      className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
    >
      <div className="flex items-center gap-4">
        <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${n > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
          <Coins className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-3xl font-extrabold leading-none tabular-nums text-slate-900">
            {n}{' '}
            <span className="text-base font-bold text-slate-500">
              {es ? (n === 1 ? 'crédito disponible' : 'créditos disponibles') : n === 1 ? 'credit available' : 'credits available'}
            </span>
          </p>
          <p className="mt-1 truncate text-xs font-semibold text-slate-500">
            {balance.hasSubscription ? (
              <>
                <span className="uppercase text-slate-700">{balance.planCode}</span>
                {balance.periodEnd && <> · {t('leads.renews')}: {formatPeriodEnd(balance.periodEnd, lang)}</>}
              </>
            ) : es ? (
              'Sin membresía activa'
            ) : (
              'No active membership'
            )}
            {balance.extra > 0 && <> · {es ? `${balance.extra} crédito(s) de $29` : `${balance.extra} × $29 credit`}</>}
          </p>
        </div>
      </div>

      {balance.status === 'past_due' && (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800">
          <CreditCard className="mt-0.5 h-4 w-4 shrink-0" /> {t('leads.pastDue')}
        </p>
      )}
      {n === 0 && balance.status !== 'past_due' && (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {es
            ? 'No tiene créditos. Compre un lead por $29 o elija una membresía.'
            : 'You have no credits. Buy a single lead for $29 or pick a membership.'}
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Link
          to={path('/jobs')}
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 text-sm font-bold text-white hover:bg-orange-600"
        >
          <Search className="h-4 w-4" /> {es ? 'Buscar leads' : 'Find leads'}
        </Link>
        <Link
          to={path(n > 0 ? CREDITS_PATH : '/pricing')}
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 text-sm font-bold text-slate-800 hover:bg-slate-50"
        >
          <CreditCard className="h-4 w-4" />
          {n > 0 ? (es ? 'Ver créditos' : 'View credits') : es ? 'Obtener créditos' : 'Get credits'}
        </Link>
      </div>
    </section>
  );
};

export default CreditsStrip;
